import {
  ChevronDownIcon,
  ChevronRightIcon,
  Copy01Icon,
  PlusIcon,
  Trash01Icon,
} from '@langchain/untitled-ui-icons';
import { LinearProgress } from '@mui/joy';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { z } from 'zod';

import GraphFillIcon from '@/Pages/Graph/icons/GraphFillIcon.svg?react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { LoadMoreButton } from '@/components/RunsTable/LoadMoreButton';
import { SecretPlainInput } from '@/components/SecretInput';
import {
  Button,
  ButtonGroup,
  ButtonProps,
  IconButton,
  Input,
  Text,
  Tooltip,
} from '@/design-system';
import {
  TabGroup,
  TabLabel,
  TabList,
  TabPanel,
} from '@/design-system/components/Tabs';
import { useHostProjectsInfinite } from '@/hooks/useSwr';
import ExternalLinkIcon from '@/icons/ExternalLinkIcon.svg?react';
import { RunSchema } from '@/types/schema';
import { cn, utils } from '@/utils/tailwind';
import { useLocalStorageState } from '@/utils/use-local-storage-state';
import { useGetGraphStudioPath } from '@/utils/useGetGraphStudioPath';

import { useGraphConnection } from './src/api/useGraphSwr';
import { STUDIO_HEADERS_LOCAL_STORAGE_KEY } from './src/constants';
import {
  useConvertTraceToThread,
  useGetGraphs,
} from './src/hooks/useConvertTraceToThread';
import { useCreateStudioClient } from './src/hooks/useCreateStudioClient';
import { StudioRenderMode } from './src/hooks/useStudioRenderMode';

const INCOMPATIBLE_SCHEMA_ERROR =
  'Cloning is disabled because your local graph has different nodes than the remote graph.';

export function GraphConnectHeadersInput(props: {
  value: Record<string, string>;
  onChange: (value: Exclude<Record<string, string>, undefined>) => void;
}) {
  const [innerValue, setInnerValue] = useState(
    Object.entries(props.value ?? {})
  );

  const onChange = (
    value:
      | [key: string, value: string]
      | Array<[key: string, value: string]>
      | null,
    index?: number
  ) => {
    const isList = (x: unknown): x is Array<[string, string]> => {
      return Array.isArray(x) && x.every((x) => Array.isArray(x));
    };

    setInnerValue((prev) => {
      let newValue = prev.slice(0);
      if (index == null) {
        if (value != null) {
          if (isList(value)) {
            newValue = value;
          } else {
            newValue.push(value);
          }
        }
      } else {
        if (value != null) {
          if (isList(value)) {
            newValue.splice(index, 1, ...value);
          } else {
            newValue[index] = value;
          }
        } else {
          newValue.splice(index, 1);
        }
      }

      props.onChange(Object.fromEntries(newValue.filter((i) => i[0] !== '')));
      return newValue;
    });
  };

  const firstMount = useRef(false);
  if (!firstMount.current) {
    if (innerValue.length === 0) onChange(['', '']);
    firstMount.current = true;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outlined" color="secondary">
          {Object.keys(props.value ?? {}).length > 0
            ? 'Edit custom headers'
            : 'Add custom headers'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="min-w-[524px] pt-3" sideOffset={18}>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <h4 className="text-md font-medium">Custom headers</h4>
            <p className="text-sm text-tertiary">
              Values will be stored locally in your browser and applied to all
              local connections regardless of the endpoint.
            </p>
          </div>
          <div className="flex flex-col gap-2">
            {innerValue.map(([key, value], idx) => (
              <div className="flex w-full items-center gap-2">
                <Input
                  type="text"
                  value={key}
                  onChange={(v) => onChange([v, value], idx)}
                  placeholder="Type name..."
                />

                <SecretPlainInput
                  type="text"
                  value={value}
                  onChange={(e) => onChange([key, e.target.value], idx)}
                  placeholder="Type value..."
                  required
                  className="min-w-[50%]"
                />

                <IconButton
                  icon={Trash01Icon}
                  label="Delete"
                  onClick={() => onChange(null, idx)}
                  variant="outlined"
                  color="secondary"
                  size="md"
                />
              </div>
            ))}
          </div>
          <div className="flex items-center justify-end gap-4">
            <Button
              type="button"
              onClick={() => onChange(['', ''])}
              variant="outlined"
              color="secondary"
              leftDecorator={PlusIcon}
            >
              Add
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export const GraphCloneThreadButton = ({
  connectionError,
  baseUrl,
  lastStudioUrl,
  localServerDisabled,
  headers,
  rootRun,
  asIcon,
  variant = 'normal',
}: {
  connectionError?: boolean;
  baseUrl: string | undefined;
  lastStudioUrl: string | undefined;
  localServerDisabled?: boolean;
  headers: Record<string, string>;
  rootRun?: RunSchema;
  asIcon?: boolean;
  variant?: ButtonProps['variant'];
}) => {
  const navigate = useNavigate();
  const { generatePath } = useGetGraphStudioPath();
  const client = useCreateStudioClient({
    baseUrl,
    headers,
  });

  const { createThreadWithStateMutation, isSchemaCompatible, stateUpdates } =
    useConvertTraceToThread({
      studioClient: client?.[0],
      rootRun,
      lastStudioUrl,
      diffUrl: lastStudioUrl !== baseUrl,
    });
  const { trigger: createThreadWithState, isMutating: isCreatingThread } =
    createThreadWithStateMutation;

  const onOpenStudioWithNewThread = async (graphId: string) => {
    const thread = await createThreadWithState({ graphId });
    if (!thread) return;
    navigate(generatePath({ threadId: thread.thread_id }));
  };

  const { data: graphs } = useGetGraphs({
    client: client?.[0],
    apiUrl: lastStudioUrl,
  });
  const compatibleGraphs = useMemo(() => {
    if (!graphs) return [];
    return graphs.filter((graph) => isSchemaCompatible(graph.nodes));
  }, [graphs, isSchemaCompatible]);

  const trigger = asIcon ? (
    <IconButton
      icon={Copy01Icon}
      label="Clone trace locally"
      variant="outlined"
      color="primary"
    />
  ) : (
    <Button
      variant={variant}
      color="primary"
      rightDecorator={ChevronRightIcon}
      loading={isCreatingThread}
    >
      Clone trace locally
    </Button>
  );

  const renderButton = () => {
    if (compatibleGraphs.length > 1) {
      return (
        <Popover>
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
          <PopoverContent
            side="right"
            align="start"
            sideOffset={16}
            className="w-full flex-col px-4 py-2"
          >
            <Text variant="sm" weight="medium" className="text-tertiary">
              SELECT A GRAPH
            </Text>
            <div className="flex flex-col items-start gap-1 pt-2">
              {compatibleGraphs.map((graph) => (
                <div className="w-full">
                  <Button
                    key={graph.graph_id}
                    onClick={() => onOpenStudioWithNewThread(graph.graph_id)}
                    variant="plain"
                    color="secondary"
                    className="w-full justify-start"
                  >
                    {graph.graph_id}
                  </Button>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      );
    }
    const graph = compatibleGraphs.length ? compatibleGraphs[0] : undefined;
    const isCompatible = graph ? isSchemaCompatible(graph.nodes) : false;
    const disabled =
      localServerDisabled || !isCompatible || stateUpdates.length === 0;
    const otherProps = graph && {
      onClick: () => onOpenStudioWithNewThread(graph.graph_id),
    };

    if (asIcon) {
      return (
        <IconButton
          icon={Copy01Icon}
          label="Clone trace locally"
          variant="outlined"
          color="primary"
          disabled={disabled}
          {...otherProps}
        />
      );
    }

    return (
      <Button
        variant={variant}
        color="primary"
        disabled={disabled}
        loading={isCreatingThread}
        {...otherProps}
      >
        Clone trace locally
      </Button>
    );
  };

  return (
    <Tooltip
      title={
        compatibleGraphs.length === 0 && !connectionError
          ? INCOMPATIBLE_SCHEMA_ERROR
          : ''
      }
    >
      {renderButton()}
    </Tooltip>
  );
};

export const GraphConnectForm = ({
  defaultHeaders,
  baseUrl,
  setBaseUrl,
  lastStudioUrl,
  setLastStudioUrl,
  onSubmit,
  openExistingGraphDisabled,
  showAdvancedFooter,
  connectionError,
  connected,
  rootRun,
  showTabs = false,
  hideFooter = false,
  onHeadersChange,
}: {
  defaultHeaders: Record<string, string>;
  baseUrl: string | undefined;
  setBaseUrl: (baseUrl: string) => void;
  lastStudioUrl: string | undefined;
  setLastStudioUrl: (lastStudioUrl: string) => void;
  onSubmit?: ({
    headers,
    hostProjectId,
  }: {
    headers?: Record<string, string>;
    hostProjectId?: string;
  }) => void;
  openExistingGraphDisabled?: boolean;
  showAdvancedFooter?: boolean;
  connectionError?: boolean;
  connected?: boolean;
  rootRun?: RunSchema;
  showTabs?: boolean;
  hideFooter?: boolean;
  onHeadersChange?: (headers: Record<string, string>) => void;
}) => {
  const [headers, setHeaders] = useState(defaultHeaders ?? {});
  const debouncedSetLastStudioUrl = useDebouncedCallback(setLastStudioUrl, 500);

  useEffect(() => {
    // on dismount, set the last studio url to the base url
    return () => {
      setLastStudioUrl(baseUrl ?? '');
    };
  }, [baseUrl, setLastStudioUrl]);

  const localServerDisabled = !connected || !baseUrl;

  const [hostProjectSearch, setHostProjectSearch] = useState('');
  const {
    hostProjects,
    isLoading: isLoadingHostProjects,
    hasMore,
    size,
    setSize,
  } = useHostProjectsInfinite({
    name_contains: hostProjectSearch,
  });
  return (
    <>
      <div className="flex flex-col gap-1.5 border-b border-secondary p-4">
        <p>
          <strong className="text-md font-medium">LangGraph Studio</strong>
        </p>
        <p className="pr-10 text-sm text-tertiary">
          Agent IDE for prototyping and debugging LangGraph applications.{' '}
          <a
            href="https://docs.langchain.com/langgraph-platform/langgraph-studio"
            target="_blank"
            className="inline-flex items-center gap-1 text-brand-green-400 focus-within:underline hover:underline"
            rel="noreferrer"
          >
            Learn more
            <ExternalLinkIcon className="h-4 w-4" />
          </a>
        </p>
      </div>
      <TabGroup className="mt-2 p-2">
        {showTabs && (
          <TabList>
            <TabLabel label="Local" />
            <TabLabel label="Deployed" />
          </TabList>
        )}
        <TabPanel>
          <form
            className="flex flex-col items-stretch gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit?.({ headers });
            }}
          >
            <p className="text-sm text-secondary">
              Enter your local endpoint below to connect:
            </p>
            <div>
              <Input
                type="text"
                placeholder="http://localhost:8123"
                value={baseUrl}
                onChange={(value) => {
                  setBaseUrl(value);
                  debouncedSetLastStudioUrl(value);
                }}
                label="Base URL"
                isError={connectionError}
                hintText={
                  connectionError
                    ? 'Connection failed. Ensure your server is running at this endpoint.'
                    : undefined
                }
              />
            </div>
            <div className="flex w-full justify-between gap-2 pt-2">
              <GraphConnectHeadersInput
                value={headers}
                onChange={(headers) => {
                  setHeaders(headers);
                  onHeadersChange?.(headers);
                }}
              />
              {hideFooter ? null : showAdvancedFooter ? (
                <div className="flex gap-2">
                  <Tooltip
                    title={
                      openExistingGraphDisabled &&
                      "Can't find original thread's host project."
                    }
                  >
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => onSubmit?.({})}
                      disabled={openExistingGraphDisabled}
                    >
                      View original thread
                    </Button>
                  </Tooltip>

                  <GraphCloneThreadButton
                    baseUrl={baseUrl}
                    lastStudioUrl={lastStudioUrl}
                    headers={defaultHeaders}
                    connectionError={connectionError}
                    localServerDisabled={localServerDisabled}
                    rootRun={rootRun}
                  />
                </div>
              ) : (
                <Button
                  type="submit"
                  variant="normal"
                  disabled={localServerDisabled}
                >
                  Connect
                </Button>
              )}
            </div>
          </form>
        </TabPanel>
        <TabPanel>
          <div className="flex max-h-[400px] flex-col gap-2 overflow-y-auto">
            <Input
              placeholder="Search deployments"
              value={hostProjectSearch}
              onChange={(value) => setHostProjectSearch(value)}
              size="sm"
            />
            {isLoadingHostProjects && <LinearProgress />}
            {hostProjects?.map((hostProject) => (
              <div key={hostProject.id} className="w-full">
                <Button
                  variant="plain"
                  color="secondary"
                  onClick={() => onSubmit?.({ hostProjectId: hostProject.id })}
                  className="w-full justify-start"
                  size="md"
                >
                  {hostProject.name}
                </Button>
              </div>
            ))}
            {hasMore && (
              <LoadMoreButton
                isInitialLoad={true}
                isLoading={isLoadingHostProjects}
                onClick={() => setSize(size + 1)}
              />
            )}
          </div>
        </TabPanel>
      </TabGroup>
    </>
  );
};

const GraphConnectButton = ({
  size,
  buttonWidth,
  buttonLabel,
  onClick,
  connected,
  supportsCloneAndOpenExisting,
}: {
  size?: 'sm' | 'md';
  buttonWidth?: 'sm' | 'md';
  buttonLabel?: string;
  onClick: () => void;
  connected: boolean;
  supportsCloneAndOpenExisting?: boolean;
}) => {
  const connectionDot = useMemo(() => {
    if (connected) {
      return (
        <Tooltip title="Connected to server">
          <div className="size-2 rounded-full bg-success-strong" />
        </Tooltip>
      );
    }
    return (
      <Tooltip title="Disconnected from server">
        <div className="size-2 rounded-full bg-quaternary" />
      </Tooltip>
    );
  }, [connected]);

  if (buttonWidth === 'sm') {
    const button = (
      <Button variant="outlined" color="primary" size="md">
        {connectionDot}
      </Button>
    );
    if (supportsCloneAndOpenExisting) {
      return <PopoverTrigger asChild>{button}</PopoverTrigger>;
    }
    return { ...button, onClick };
  }

  if (connected) {
    return (
      <ButtonGroup variant="outlined" color="primary" size={size}>
        <Button
          variant="outlined"
          className="flex items-center gap-2.5"
          onClick={onClick}
        >
          <div className="flex items-center gap-2">
            {connectionDot}
            <Text variant="sm">{buttonLabel ?? 'LangGraph Studio'}</Text>
          </div>
        </Button>
        <PopoverTrigger asChild>
          <IconButton icon={ChevronDownIcon} label="Studio settings" />
        </PopoverTrigger>
      </ButtonGroup>
    );
  }

  return (
    <PopoverTrigger asChild>
      <Button
        variant="outlined"
        className="flex items-center gap-2.5"
        size={size}
        color={connected ? 'primary' : 'secondary'}
      >
        <div className="flex items-center gap-2">
          {connectionDot}
          <Text variant="sm">{buttonLabel ?? 'LangGraph Studio'}</Text>
        </div>
      </Button>
    </PopoverTrigger>
  );
};

export const GraphConnectPopover = ({
  size,
  buttonWidth,
  rootRun,
  buttonLabel,
  connectionTypes,
  graphUrl,
}: {
  size?: 'sm' | 'md';
  buttonWidth?: 'sm' | 'md';
  rootRun?: RunSchema;
  buttonLabel?: string;
  connectionTypes?: ('local' | 'deployed')[];
  graphUrl?: string | null;
}) => {
  const supportsCloneAndOpenExisting = (connectionTypes?.length ?? 0) > 1;
  const navigate = useNavigate();
  const hostProjectId =
    rootRun?.extra?.metadata?.['LANGSMITH_HOST_PROJECT_ID'] ??
    rootRun?.extra?.metadata?.['langsmith_host_project_id'];

  const { generatePath, lastStudioUrl, setLastStudioUrl } =
    useGetGraphStudioPath();

  const { error: lastActiveError, data: lastActive } = useGraphConnection({
    studioUrl: lastStudioUrl,
  });

  const [localHeaders, setLocalHeaders] = useLocalStorageState(
    STUDIO_HEADERS_LOCAL_STORAGE_KEY,
    {},
    z.record(z.string(), z.string())
  );
  const [baseUrl, setBaseUrl] = useState(lastStudioUrl);

  const openExistingThread = (_hostProjectId?: string) => {
    navigate(
      generatePath({
        threadId: rootRun?.extra?.metadata?.thread_id,
        hostProjectId: _hostProjectId ?? hostProjectId,
        render: rootRun?.extra?.metadata?.thread_id
          ? StudioRenderMode.TRACE
          : StudioRenderMode.INTERACT,
      })
    );
  };

  return (
    <Popover>
      <GraphConnectButton
        size={size}
        buttonWidth={buttonWidth}
        buttonLabel={buttonLabel}
        onClick={() => openExistingThread()}
        connected={hostProjectId || lastActive}
        supportsCloneAndOpenExisting={supportsCloneAndOpenExisting}
      />
      <PopoverContent
        align="end"
        className={cn(
          'flex w-full max-w-[550px] flex-col p-0',
          supportsCloneAndOpenExisting ? 'max-w-[550px]' : 'max-w-[400px]'
        )}
      >
        <GraphConnectForm
          defaultHeaders={localHeaders}
          baseUrl={baseUrl}
          setBaseUrl={setBaseUrl}
          lastStudioUrl={lastStudioUrl}
          setLastStudioUrl={setLastStudioUrl}
          onSubmit={({ headers, hostProjectId }) => {
            setLastStudioUrl(baseUrl);
            if (headers) {
              setLocalHeaders(headers);
            }
            openExistingThread(hostProjectId);
          }}
          openExistingGraphDisabled={!graphUrl}
          showAdvancedFooter={supportsCloneAndOpenExisting}
          connected={!!lastActive}
          connectionError={!!lastActiveError}
          rootRun={rootRun}
          onHeadersChange={setLocalHeaders}
        />
      </PopoverContent>
    </Popover>
  );
};

export const NavLinksGraphConnectPopover = (props: { expanded: boolean }) => {
  const navigate = useNavigate();
  const { lastStudioUrl, setLastStudioUrl, generatePath } =
    useGetGraphStudioPath();
  const [baseUrl, setBaseUrl] = useState(lastStudioUrl);

  const { error: lastActiveError, data: lastActive } = useGraphConnection({
    studioUrl: lastStudioUrl,
  });

  const [localHeaders, setLocalHeaders] = useLocalStorageState(
    STUDIO_HEADERS_LOCAL_STORAGE_KEY,
    {},
    z.record(z.string(), z.string())
  );

  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      {props.expanded ? (
        <PopoverTrigger
          className={cn(
            utils.button,
            'flex w-full select-none items-center gap-1.5 rounded-none px-3 py-1.5 text-xs text-secondary outline-none'
          )}
        >
          <GraphFillIcon className="h-4 w-4" />
          <div className="line-clamp-1 font-semibold">LangGraph Studio</div>
        </PopoverTrigger>
      ) : (
        <Tooltip title="LangGraph Studio" side="right">
          <div className="flex w-full items-center justify-center gap-0">
            <PopoverTrigger
              className={cn(
                utils.button,
                'flex flex-col items-center rounded-md p-2'
              )}
            >
              <GraphFillIcon className="h-4 w-4" />
            </PopoverTrigger>
          </div>
        </Tooltip>
      )}
      <PopoverContent
        side="right"
        align="start"
        sideOffset={!props.expanded ? 12 : undefined}
        className="flex w-full max-w-[360px] flex-col p-0"
      >
        <GraphConnectForm
          baseUrl={baseUrl}
          setBaseUrl={setBaseUrl}
          lastStudioUrl={lastStudioUrl}
          setLastStudioUrl={setLastStudioUrl}
          defaultHeaders={localHeaders}
          onSubmit={({ headers }) => {
            setIsOpen(false);
            setLastStudioUrl(baseUrl);
            if (headers) {
              setLocalHeaders(headers);
            }
            navigate(generatePath({}));
          }}
          connected={!!lastActive}
          connectionError={!!lastActiveError}
          onHeadersChange={setLocalHeaders}
        />
      </PopoverContent>
    </Popover>
  );
};

export const CloneThreadButtonWithForm = ({
  rootRun,
  size,
}: {
  rootRun: RunSchema;
  size?: 'sm' | 'md';
}) => {
  const { lastStudioUrl, setLastStudioUrl } = useGetGraphStudioPath();

  const { error: lastActiveError, data: lastActive } = useGraphConnection({
    studioUrl: lastStudioUrl,
  });

  const [localHeaders, setLocalHeaders] = useLocalStorageState(
    STUDIO_HEADERS_LOCAL_STORAGE_KEY,
    {},
    z.record(z.string(), z.string())
  );
  const [baseUrl, setBaseUrl] = useState(lastStudioUrl);
  return (
    <Popover>
      <ButtonGroup variant="outlined" color="primary">
        <GraphCloneThreadButton
          baseUrl={baseUrl}
          connectionError={lastActiveError}
          lastStudioUrl={lastStudioUrl}
          localServerDisabled={!baseUrl || !lastActive}
          headers={localHeaders}
          rootRun={rootRun}
          variant="outlined"
          asIcon={size === 'sm'}
        />
        <PopoverTrigger asChild>
          <IconButton icon={ChevronDownIcon} label="Studio settings" />
        </PopoverTrigger>
      </ButtonGroup>
      <PopoverContent
        className={cn('flex w-full max-w-[400px] flex-col p-0')}
        align="end"
      >
        <GraphConnectForm
          baseUrl={baseUrl}
          setBaseUrl={setBaseUrl}
          lastStudioUrl={lastStudioUrl}
          setLastStudioUrl={setLastStudioUrl}
          defaultHeaders={localHeaders}
          hideFooter={true}
          onHeadersChange={setLocalHeaders}
        />
      </PopoverContent>
    </Popover>
  );
};
